import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { fetchUserMemberships } from '../../../services/MasterAdmin/membershipService';
import MembershipPanelShell from './MembershipPanelShell';
import { MembershipError, MembershipLoading, StatusBadge } from './MembershipAlerts';
import { msInput, msPrimaryBtn, msSecondaryBtn, msTableWrap, msTd, msTh, formatMembershipDateTime } from './membershipUi';
import MembershipMemberDetailPage from './MembershipMemberDetailPage';

const MembershipMembersPanel: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchUserMemberships({
        q: query || undefined,
        status: statusFilter || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load memberships');
    } finally {
      setLoading(false);
    }
  }, [page, query, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  // Show detail page
  if (selectedId) {
    return (
      <MembershipMemberDetailPage
        membershipId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <MembershipPanelShell title="Members">
      <MembershipError message={error} />

      {/* ── Search & filter ── */}
      <div className="mb-5 flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
            placeholder="Search by name, phone, or plan…"
            className={`${msInput} w-full pl-9`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className={msInput}
        >
          <option value="">All statuses</option>
          {['pending', 'active', 'expired', 'cancelled', 'failed', 'refunded'].map((s) => (
            <option key={s} value={s} className="capitalize">{s}</option>
          ))}
        </select>
        <button type="button" className={msPrimaryBtn} onClick={() => { setPage(1); void load(); }}>
          Search
        </button>
      </div>

      {loading ? <MembershipLoading /> : null}

      {!loading && (
        <>
          <div className={msTableWrap}>
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className={msTh}>Member</th>
                  <th className={msTh}>Plan</th>
                  <th className={msTh}>Status</th>
                  <th className={msTh}>Valid until</th>
                  <th className={msTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const daysLeft = row.endDate
                    ? Math.max(0, Math.ceil((new Date(row.endDate).getTime() - Date.now()) / 86400000))
                    : null;
                  const isExpiring = row.status === 'active' && daysLeft != null && daysLeft <= 14;
                  return (
                    <tr
                      key={row.userMembershipId}
                      className="border-t border-gray-100 transition-colors hover:bg-gray-50/60 dark:border-gray-800 dark:hover:bg-gray-800/40 cursor-pointer"
                      onClick={() => setSelectedId(row.userMembershipId)}
                    >
                      <td className={msTd}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            {(row.user?.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {row.user?.name || row.userId}
                            </div>
                            <div className="text-xs text-gray-400">{row.user?.phone_number}</div>
                          </div>
                        </div>
                      </td>
                      <td className={msTd}>
                        <span className="font-medium text-gray-800 dark:text-gray-200">{row.planName}</span>
                      </td>
                      <td className={msTd}>
                        <StatusBadge status={row.status} />
                      </td>
                      <td className={msTd}>
                        <div>
                          {formatMembershipDateTime(row.endDate) || '—'}
                        </div>
                        {isExpiring && (
                          <div className="mt-0.5 text-xs font-semibold text-orange-500">
                            ⚠ {daysLeft} days left
                          </div>
                        )}
                      </td>
                      <td className={msTd} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                          onClick={() => setSelectedId(row.userMembershipId)}
                        >
                          View details →
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr>
                    <td className={`${msTd} py-10 text-center text-gray-400`} colSpan={5}>
                      No memberships found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
            <span>{total} total · page {page} of {Math.max(1, totalPages)}</span>
            <div className="flex gap-2">
              <button
                type="button"
                className={msSecondaryBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <button
                type="button"
                className={msSecondaryBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </MembershipPanelShell>
  );
};

export default MembershipMembersPanel;
